import { createContext, ReactNode, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

type TabsContextType = {
  activeTab: string
  registerTabValue: (tabValue: string) => string
  setActiveTab: (newActiveTab: string) => string
  tabValues: Array<string>
  unregisterTabValue: (tabValue: string) => void
}

export const TabsContext = createContext<TabsContextType>({
  activeTab: '',
  registerTabValue: (tabValue) => tabValue,
  setActiveTab: (newActiveTab) => newActiveTab,
  tabValues: [],
  unregisterTabValue: () => undefined,
})

export type TabsContextProviderProps = {
  children: ReactNode
  defaultValue?: string
  onChange?: (tab: string) => void
}

export const TabsContextProvider = ({
  children,
  defaultValue,
  onChange,
}: TabsContextProviderProps) => {
  const [activeTab, setActiveTab] = useState<string>(defaultValue ?? '')
  const [tabValues, setTabValues] = useState<Array<string>>([])

  const router = useRouter()

  useEffect(() => {
    const pathSegments = router.asPath.split('/')
    const lastSegment = pathSegments[pathSegments.length - 1]
    const tabName = lastSegment.split('?')[0]

    if (tabName && tabName !== activeTab && tabValues.includes(tabName)) {
      setActiveTab(tabName)
    }
  }, [router.asPath, tabValues, activeTab])

  return (
    <TabsContext.Provider
      value={{
        activeTab,
        registerTabValue: (tabValue: string) => {
          setTabValues((prevTabValues) => [...prevTabValues, tabValue])

          return tabValue
        },
        setActiveTab: (newActiveTab: string) => {
          setActiveTab(newActiveTab)

          if (onChange) {
            onChange(newActiveTab)
          }

          return newActiveTab
        },
        tabValues,
        unregisterTabValue: (tabValue: string) => {
          setTabValues((prevTabValues) =>
            prevTabValues.filter((prevTabValue) => prevTabValue === tabValue),
          )
        },
      }}
    >
      {children}
    </TabsContext.Provider>
  )
}
